<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form'
import AutoFormLabel from '@/ui-plus/auto-form/AutoFormLabel.vue'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { DoorOpen, MapPin } from "lucide-vue-next";
import { AutoForm } from '@/ui-plus/auto-form'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { z } from "zod";
import { useFormPlus } from '@/ui-plus/form/useFormPlus'
import { Loader2 } from 'lucide-vue-next'
import AutoFormFieldCountry from '@/ui-plus/auto-form/AutoFormFieldCountry.vue'
import { computed, onMounted, ref, watch } from 'vue';
import { toast } from "@/ui-plus/sonner";
import { currentWebsiteId, loadMyWebsites } from '@partner-spa-vue/stores/MyWebsitesStore';
import { zmgPublicCacheClient, zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache';
import { DependencyType } from '@/ui-plus/auto-form/interface';
import GeolocationMap from '@/ui-plus/geolocation-selection-map/GeolocationMap.vue';
import { loadLeafletCDN } from '@/leaflet/loadLeafletCDN';
import { ipwhois } from '@/ui-plus/geolocation-selection-map/ipwhois';
import { getAddress } from '@lib/utils/checkGeocodeDistance';
import { allCountriesAndStatesWithMultipleSalesTaxJurisdiction } from '@lib/utils/sales_tax_rates';

const schema = z.object({
    address1: z.string().min(1, 'Required'),
    address2: z.string().optional(),
    city: z.string().min(1, 'Required'),
    postalCode: z.string().min(1, 'Required'),
    state: z.string().optional(),
    country: z.string().min(1, 'Required'),
})

const form = useForm({
    validationSchema: toTypedSchema(schema),
})

const COUNTRIES_WITH_STATES = [
    'US', // United States
    'CA', // Canada
    'AU', // Australia
    'BR', // Brazil
    'IN', // India
    'MX', // Mexico
    'AR', // Argentina
    'MY', // Malaysia
    'ID', // Indonesia
    'RU', // Russia
    'CN', // China
    'JP', // Japan
    'DE', // Germany
    'ES', // Spain
    'IT', // Italy
    'GB', // United Kingdom (has countries/regions)
    'FR', // France (has regions)
    'PH', // Philippines
    'PK', // Pakistan
    'NG', // Nigeria
    'VN', // Vietnam
    'ZA', // South Africa
    'TH', // Thailand
    'MM', // Myanmar (Burma)
    'KR', // South Korea
    'CO', // Colombia
    'VE', // Venezuela
]; // Add more as needed

let lat: number | null = null;
let lng: number | null = null;

const loadData = async () => {
    isLoading.value = true;
    console.log('loadData', currentWebsiteId.value);
    // // sleep 1 second
    // await new Promise(resolve => setTimeout(resolve, 1000));
    const { data: address, error } = await (await zmgPublicCacheClient.website.partner_address[':id']['data.json'].$get({
        param: {
            id: currentWebsiteId.value ?? '',
        },
    })).json()
    if (error) {
        handleError(error, form);
    } else {
        if (address) {
            // form.setFieldValue('country', data.address?.country ?? undefined);
            // form.setFieldValue('address1', data.address?.address1 ?? undefined);
            // form.setFieldValue('address2', data.address?.address2 ?? undefined);
            // form.setFieldValue('city', data.address?.city ?? undefined);
            // form.setFieldValue('state', data.address?.state ?? undefined);
            // form.setFieldValue('postalCode', data.address?.postalCode ?? undefined);
            // form.setFieldValue('postalCode', data.address?.postalCode ?? undefined);
            // for (const key in data.address) {
            //     const typedKey = key as keyof typeof schema.shape;
            //     if ((data.address as any)[key]) {
            //         form.setFieldValue(typedKey, (data.address as any)[key]);
            //     }
            // }
            for (const key in address) {
                const typedKey = key as keyof typeof schema.shape;
                if (address[typedKey]) {
                    form.setFieldValue(typedKey, address[typedKey]);
                }
            }
            // if (address?.lat && address?.lng) {
            //     mapLocation.value.lat = address.lat;
            //     mapLocation.value.lng = address.lng;
            // }
            lat = address.lat;
            lng = address.lng;
        }
        if (!address || !address.country) {
            const ipWhoisResult = await ipwhois()
            form.setFieldValue('country', ipWhoisResult?.country ?? '');
            form.setFieldValue('state', ipWhoisResult?.region1 ?? '');
        }
    }
    isLoading.value = false;
}

onMounted(() => {
    loadData();
    loadLeafletCDN(true);
})



const isDialogOpen = ref(false);
const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

async function onSubmit(_values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        isDialogOpen.value = true;
        // console.log('values', values);
        // values.address1, form.values.country?.toLowerCase()
        // mapLocation.value.address = values.address1;
        // mapLocation.value.country = values.country.toLowerCase();
    })
}

async function onMapConfirm(result: { lat: number, lng: number }) {
    isDialogOpen.value = false;
    // console.log('mapLocation', mapLocation.value);
    isLoading.value = true;
    // const reverseGeocodeResult = await reverseGeocode(lat, lng);
    // console.log('reverseGeocodeResult', reverseGeocodeResult);
    const { data, error } = await (await zmgAdminOnlyCacheUpdateClient.website.partner_address[':id'].$patch({
        param: {
            id: currentWebsiteId.value ?? '',
        },
        json: {
            ...form.values,
            lat: result.lat,
            lng: result.lng,
        }
    })).json()
    if (error) {
        handleError(error, form);
    } else {
        // Success
        console.log('data', data);
        toast.success(`Saved`);
        lat = result.lat;
        lng = result.lng;
    }
    // wait 1 second
    // await new Promise(resolve => setTimeout(resolve, 1000));
    isLoading.value = false;
    await loadMyWebsites();
}

const address1Label = ref('Street address (Address 1)');
const address2Label = ref('Apt, suite, unit, building, floor, etc. (Address 2)');
const hideAddress2Label = ref(false);
const addressStateLabel = ref('State/Province/Territory');
const addressCityLabel = ref('City');
const addressPostalCodeLabel = ref('Postcode/Zip');

watch(() => form.values.country, (newCountry) => {
    if (newCountry === 'US') {
        address1Label.value = 'Street address';
        address2Label.value = 'Apt, suite, unit, building, floor, etc.';
        hideAddress2Label.value = true;
        addressCityLabel.value = 'City';
        addressStateLabel.value = 'State';
        addressPostalCodeLabel.value = 'Postcode';
    } else if (newCountry === 'GB') {
        address1Label.value = 'Address line 1 (or company name)';
        address2Label.value = 'Address line 2 (optional)';
        addressCityLabel.value = 'Town/City';
        addressStateLabel.value = 'State/County (if applicable)';
        hideAddress2Label.value = false;
        addressPostalCodeLabel.value = 'Postcode';
    } else if (newCountry === 'CA') {
        address1Label.value = 'Address';
        address2Label.value = 'Apt, suite, unit, building (Address 2)';
        hideAddress2Label.value = true;
        addressStateLabel.value = 'Province/Territory';
        addressCityLabel.value = 'City';
        addressPostalCodeLabel.value = 'Postal code';
    } else {
        address1Label.value = 'Street address (Address 1)';
        address2Label.value = 'Apt, suite, unit, building, floor, etc. (Address 2)';
        addressStateLabel.value = 'State/Province/Territory';
        addressCityLabel.value = 'City';
        hideAddress2Label.value = true;
        addressPostalCodeLabel.value = 'Postcode/Zip';
    }
});

const computedStates = computed(() => {
    return Object.entries(
        allCountriesAndStatesWithMultipleSalesTaxJurisdiction[form.values.country ?? ''] || {}
    ).sort() || [];
});

const isStateFieldVisible = computed(() => {
    return COUNTRIES_WITH_STATES.includes(form.values.country ?? '');
});

const IS_DEVELOPMENT = import.meta.env.DEV;

</script>

<template>

    <Dialog v-model:open="isDialogOpen">
        <DialogContent
                       class="flex h-full min-h-full min-w-full flex-col p-0 sm:p-2 md:h-[70vh] md:min-h-[70vh] md:min-w-[80vw] lg:min-w-[50vw]">
            <DialogHeader>
                <DialogTitle class="flex items-center gap-2">
                    <DoorOpen />
                    Confirm building entrance/door location
                </DialogTitle>
                <!-- <DialogDescription class="hidden text-sm sm:block">
                    Move the map to your entrance/door and click Confirm
                </DialogDescription> -->
            </DialogHeader>
            <GeolocationMap :address="getAddress(form.values)"
                            :country="form.values.country!"
                            :lat="lat!"
                            :lng="lng!"
                            @done="onMapConfirm"
                            class="flex-1 overflow-hidden" />
            <!-- <div class="grid gap-4 py-4">
                     <div class="grid grid-cols-4 items-center gap-4">
                         <Label for="name" class="text-right"> Name </Label>
                         <Input id="name" value="Pedro Duarte" class="col-span-3" />
                     </div>
                     <div class="grid grid-cols-4 items-center gap-4">
                         <Label for="username" class="text-right"> Username </Label>
                         <Input id="username" value="@peduarte" class="col-span-3" />
                     </div>
                 </div>
                 -->
            <!-- <DialogFooter>
                <Button type="button"
                        @click="onMapConfirm"
                        :disabled="isLoading"
                        class="w-full my-6">
                    <Loader2 v-if="isLoading"
                             class="animate-spin" />
                    Save
                </Button>
            </DialogFooter> -->
        </DialogContent>
    </Dialog>

    <Button variant="outline"
            v-if="IS_DEVELOPMENT"
            @click="loadData">refresh</Button>

    <AutoForm class="space-y-6"
              :schema="schema"
              :dependencies="[
                {
                    sourceField: 'country',
                    type: DependencyType.HIDES,
                    targetField: 'state',
                    when: (country) => !COUNTRIES_WITH_STATES.includes(country),
                }
            ]"
              :field-config="{
                country: {
                    component: AutoFormFieldCountry,
                    label: 'Country',
                    inputProps: {
                        // disabled: true,
                        autocomplete: 'shipping country',
                    },
                },
                address1: {
                    // component: AutoFormFieldAddress,
                    // description: 'Enter your full address',
                    label: address1Label,
                    inputProps: {
                        placeholder: 'Enter your full address',
                        autocomplete: 'shipping address-line1',
                    },
                },
                address2: {
                    label: address2Label,
                    hideLabel: hideAddress2Label,
                    inputProps: {
                        placeholder: hideAddress2Label ? address2Label : '',
                        autocomplete: 'shipping address-line2',
                    },
                },
                city: {
                    label: addressCityLabel,
                    inputProps: {
                        autocomplete: 'shipping address-level2',
                    },
                },
                state: {
                    label: addressStateLabel,
                    inputProps: {
                        autocomplete: 'shipping address-level1',
                    },
                },
                postalCode: {
                    label: addressPostalCodeLabel,
                    inputProps: {
                        autocomplete: 'shipping postal-code',
                    },
                }
            }"
              :form="form"
              @submit="onSubmit">

        <template #state="allProps">
            <div>
                <FormField v-slot="slotProps"
                           name="state"
                           v-if="isStateFieldVisible">
                    <FormItem>
                        <AutoFormLabel v-if="!allProps.config?.hideLabel"
                                       :required="computedStates.length > 0">
                            {{ allProps.config?.label }}
                        </AutoFormLabel>
                        <FormControl>
                            <slot v-bind="slotProps">
                                <select v-if="computedStates.length > 0"
                                        v-bind="{ ...slotProps.componentField as any, ...allProps.config?.inputProps }"
                                        class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        v-model="form.values.state"
                                        @change="(e: Event) => form.setFieldValue('state', (e.target as HTMLSelectElement).value)">
                                    <option value=""
                                            disabled>
                                        Select state
                                    </option>
                                    <option v-for="state in computedStates"
                                            :key="state[0]"
                                            :value="state[0]">
                                        {{ state[1] }}
                                    </option>
                                </select>
                                <Input v-else
                                       v-bind="{ ...slotProps.componentField as any, ...allProps.config?.inputProps }" />
                            </slot>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
            </div>
        </template>

        <div class="text-sm font-medium text-destructive"
             v-if="globalError">
            {{ globalError }}
        </div>
        <div>
            <Button type="submit"
                    :disabled="isLoading"
                    class="w-full my-6">
                <Loader2 v-if="isLoading"
                         class="animate-spin" />
                Set location
                <MapPin />
            </Button>
        </div>
    </AutoForm>
</template>